import { createElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '~/lib/api-client';
import { useConfirm, useConfirmWithOption } from '~/components/confirm-dialog';
import { BuildConflictDescription, type BuildConflictView } from '~/features/coding/build-conflict-description';
import { publishOrConfirm } from '~/lib/held-publish';
import { budgetKeys, codingKeys, feedKeys, filesOverviewKeys, attentionKeys } from '~/lib/query-keys';
import { budgetRefused } from '~/features/limits/budget-refusal';
import { documentKeys } from '~/hooks/queries/use-documents';
import type { AppAudience, CodingSessionView } from '~/features/coding/coding-events';
import type { DesignBriefView, DesignCreatePayload, DesignDirective, DesignSuggestion } from '~/features/coding/design-direction';

const isTextOrNull = (value: unknown) => value === null || typeof value === 'string';
const isOptionalFlag = (value: unknown) => value === undefined || typeof value === 'boolean';

function isBuildConflictVersion(version: BuildConflictView['versions'][number] | undefined): boolean {
  return !!version && typeof version.path === 'string' && isTextOrNull(version.current) && isTextOrNull(version.built)
    && isOptionalFlag(version.binary) && isOptionalFlag(version.truncated);
}

function isBuildConflict(conflict: BuildConflictView | undefined): conflict is BuildConflictView {
  if (!conflict || typeof conflict.operationId !== 'string') return false;
  const pathsValid = Array.isArray(conflict.paths) && conflict.paths.every((path) => typeof path === 'string');
  const versionsValid = Array.isArray(conflict.versions) && conflict.versions.every(isBuildConflictVersion);
  return pathsValid && versionsValid;
}

/**
 * Create a coding session (FRONT-333). The backend slugifies the name,
 * ensures the sandbox project repo, and returns the fresh session row.
 *
 * FRONT-1787: `context` is the optional upfront description, omitted when
 * empty. A repeat project name is refused by the route (409 `project_exists`)
 * rather than reusing that project's repo, so there is never existing content
 * here to preserve or clear — clearing lives on the editor's PUT instead.
 *
 * FRONT-2382: `design` is the optional "Design direction" section body —
 * built by `buildDesignCreatePayload` (`~/features/coding/design-direction`),
 * which already omits the field entirely when the flag is off or nothing
 * was given, so this hook just forwards whatever it's handed.
 */
export function useCreateCodingSession() {
  const queryClient = useQueryClient();
  const refreshBudget = () => void queryClient.invalidateQueries({ queryKey: budgetKeys.all });
  return useMutation({
    mutationFn: (input: CreateCodingSessionInput) => postCreateCodingSession(input, refreshBudget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.all });
    },
  });
}

/** FRONT-2938: the create request; a spent budget refuses it with its sentence, before anything is made. */
/** FRONT-3138: the name is optional (EDDY names the project) and reference files may ride along. */
export type CreateCodingSessionInput = {
  name?: string;
  context?: string;
  design?: DesignCreatePayload;
  attachments?: Array<{ fileId: string }>;
};

export async function postCreateCodingSession(
  input: CreateCodingSessionInput,
  refreshBudget: () => void,
) {
  try {
    return await api.post<{
      session: CodingSessionView;
      repoCreated: boolean;
      // FRONT-1787: null when no context was sent, false when it was and the
      // write failed. The project still exists either way — the write is
      // best-effort — so the client is the only place that can tell the user
      // their grounding did not land.
      contextWritten: boolean | null;
      // FRONT-3138: null when no reference files were sent, false when
      // staging them into the project failed.
      referencesStaged?: boolean | null;
    }>('/api/coding/sessions', {
      name: input.name,
      title: input.name,
      ...(input.context !== undefined ? { context: input.context } : {}),
      ...(input.design !== undefined ? { design: input.design } : {}),
      ...(input.attachments?.length ? { attachments: input.attachments } : {}),
    });
  } catch (err) {
    throw budgetRefused(err, refreshBudget) ?? err;
  }
}

/**
 * FRONT-2382: "Suggest directions from the web" / "More suggestions" on the
 * New-project form's Design direction section. Standalone — there is no
 * session id yet, this runs before a project exists. The caller grows
 * `seenTitles`/`seenImageUrls` across calls so a repeat click never repeats
 * a card (`POST /api/coding/design-suggestions`, `src/coding/routes.ts`).
 */
export function useDesignSuggestions() {
  return useMutation({
    mutationFn: (input: {
      name?: string;
      context?: string;
      inspiration?: string;
      seenTitles?: string[];
      seenImageUrls?: string[];
    }) => api.post<{ suggestions: DesignSuggestion[] }>('/api/coding/design-suggestions', input),
  });
}

/**
 * FRONT-1787: replace the project's context (CODE-CONTEXT.md). An empty
 * string deletes the file. Takes effect on the session's NEXT run — the
 * in-flight one already has its system prompt, which is why the dialog says
 * so rather than implying a live change.
 */
export function useSetCodingSessionContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, context }: { sessionId: string; context: string }) =>
      api.put<{ context: string | null }>(`/api/coding/sessions/${sessionId}/context`, {
        context,
      }),
    onSuccess: (data, variables) => {
      // Write the server's own answer into the cache SYNCHRONOUSLY, before
      // invalidating. `invalidateQueries` only schedules a refetch, so a
      // consumer that resets its draft in its own onSuccess would otherwise
      // re-seed from the PRE-save value and the box would visibly revert to
      // the old text with a dirty Save bar over it — including, on a clear,
      // the text that was just deleted reappearing.
      queryClient.setQueryData(codingKeys.context(variables.sessionId), {
        context: data.context,
        readable: true,
      });
      queryClient.invalidateQueries({ queryKey: codingKeys.context(variables.sessionId) });
    },
  });
}

/**
 * FRONT-2382: edit the session's Design direction brief from the
 * Project-context dialog — text/chips, or dropping a reference by id
 * (`PUT /api/coding/sessions/:id/design`). Mirrors
 * `useSetCodingSessionContext`'s cache-then-invalidate order above, for the
 * same reason: a consumer that re-seeds its draft from its own `onSuccess`
 * must see the SAVED value, not the pre-save one.
 */
export function useUpdateCodingDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      ...patch
    }: {
      sessionId: string;
      inspiration?: string;
      directives?: DesignDirective[];
      removeReferenceIds?: string[];
    }) => api.put<{ design: DesignBriefView | null }>(`/api/coding/sessions/${sessionId}/design`, patch),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(codingKeys.design(variables.sessionId), data);
      queryClient.invalidateQueries({ queryKey: codingKeys.design(variables.sessionId) });
    },
  });
}

/**
 * Mark a build seen (FRONT-1681, build lifecycle round) — open = seen, the
 * same green→gray step `useMarkChatSeen` (`use-chat-status.ts`) gets for a
 * chat. Flips server-side (survives reload, crosses devices) via the same
 * `chat_seen` marker, namespaced under the build's id.
 */
export function useMarkCodingSessionSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.post(`/api/coding/sessions/${sessionId}/seen`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    },
  });
}

/**
 * Save the session's repo into Apps (FRONT-336, FRONT-1668, FRONT-1775).
 * The backend bundles the project as a multi-file app and pushes it
 * through the existing Apps ramp. Every save — first or re-save — now
 * goes LIVE unconditionally (Decision 7): lifecycle stopped being a UI
 * concept this call controls, and the audience badge (Only you / Shared /
 * Public) is the only state a user sees. `visibility`/`workspaceShared`
 * are untouched by a re-save (the create-only invariant), so a shared app
 * stays shared. The one refusal: an app the owner explicitly Archived
 * rejects (409, `code: "archived"`) rather than being silently revived.
 */
export interface PublishCodingSessionResult {
  /** The publish this made, named only when it published through Git (FRONT-3368). */
  publishId?: number;
  slug: string;
  title: string;
  url: string;
  updated: boolean;
  /** The app's real status after this save — always "live" unless the
   *  request was refused (see the docblock above). */
  status: 'draft' | 'live' | 'archived';
  /** FRONT-1879: WHO CAN OPEN IT, decided server-side (it needs all three
   *  columns plus the null-owner rule, so the client must not re-derive it).
   *  This is what the save toast reports — lifecycle is not an audience. */
  audience: AppAudience;
  entryPath: string;
  inlined: string[];
  warnings: string[];
}

/** FRONT-2944: a publish held for its findings asks the owner, and "Publish anyway" sends it again; null when they chose not to. */
export function usePublishCodingSession() {
  const confirm = useConfirm();
  const choose = useConfirmWithOption();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      let preparedOperationId: string | undefined;
      let resolution: { operationId: string; choice: 'local' | 'current' } | undefined;
      for (;;) {
        try {
          if (resolution) {
            const prepared = await api.post<{ status: 'prepared'; operationId: string }>(`/api/coding/sessions/${sessionId}/build-reconcile`, resolution);
            preparedOperationId = prepared.operationId;
            resolution = undefined;
          }
          return await publishOrConfirm(
            (token) => api.post<PublishCodingSessionResult>(`/api/coding/sessions/${sessionId}/publish`, { ...(preparedOperationId ? { preparedOperationId } : {}), ...(token ? { confirm: token } : {}) }),
            confirm,
          );
        } catch (error) {
          const body = error instanceof ApiError && error.status === 409 ? error.body as {code?:unknown;buildConflict?:BuildConflictView}|undefined : undefined;
          const conflict = body?.code === 'build_conflict' ? body.buildConflict : undefined;
          if (!isBuildConflict(conflict)) throw error;
          const decision = await choose({ title: 'Choose the files to keep', description: createElement(BuildConflictDescription, { conflict }), confirmText: 'Resolve and continue', cancelText: 'Cancel publish', option: { label: 'Keep current files instead of replacing them with built files', defaultChecked: true } });
          if (!decision.confirmed) return null;
          preparedOperationId = undefined;
          resolution = { operationId: conflict.operationId, choice: decision.checked ? 'current' : 'local' };
        }
      }
    },
  });
}

/**
 * Save the session as a document in Files (FRONT-334). The backend renders
 * the event tape into a journal and creates-or-updates the one document
 * linked to this session, so re-saving never duplicates.
 */
export function useSaveCodingSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ filename: string; title: string; updated: boolean }>(
        `/api/coding/sessions/${sessionId}/save`,
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
  });
}

/**
 * Generate a handover brief for the session (FRONT-646): an LLM-synthesized,
 * decision-level carry-forward summary, stored on the session so "Clear &
 * continue" (FRONT-647) can seed a fresh context from it.
 */
export function useHandoverCodingSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ handover: string }>(`/api/coding/sessions/${sessionId}/handover`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    },
  });
}

/**
 * Clear & continue (FRONT-647): reset the session's SDK conversation while
 * keeping the sandbox repo, so the next run starts a fresh context seeded from
 * the handover. The backend refuses if no handover exists yet.
 */
export function useClearCodingSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ cleared: boolean }>(`/api/coding/sessions/${sessionId}/clear`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    },
  });
}

/**
 * FRONT-2776: delete a project — the session, its history and its repo folder.
 * A published app stays; it only loses its link back to the project, as does
 * a journal saved to Files.
 */
export function useDeleteCodingSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.delete<{ deleted: boolean; repoRemoved: boolean }>(`/api/coding/sessions/${sessionId}`),
    onSuccess: (_data, sessionId) => {
      queryClient.removeQueries({ queryKey: codingKeys.detail(sessionId) });
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
  });
}

/**
 * Toggle a session's auto-handover+clear setting (FRONT-648). When on, the
 * backend auto-handovers and resets the context after a run that ends above the
 * session's threshold (FRONT-2884: on by default, 40% unless the session picks another).
 */
/**
 * FRONT-2219: set the session's built-in capability groups (web, knowledge,
 * agents) — one or several at once (FRONT-2473's master switch sends all
 * three). Takes effect on the next run.
 */
export type CodingCapability = 'web' | 'knowledge' | 'agents';
export function useSetCodingCapability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, changes }: { sessionId: string; changes: Partial<Record<CodingCapability, boolean>> }) =>
      api.post<{ webToolsEnabled: boolean; knowledgeToolsEnabled: boolean; agentToolsEnabled: boolean }>(
        `/api/coding/sessions/${sessionId}/capabilities`,
        changes,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    },
  });
}

/** FRONT-2220: replace the session's attached Connections MCP servers. */
export function useSetCodingMcpServers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, servers }: { sessionId: string; servers: string[] }) =>
      api.put<{ attached: string[] }>(`/api/coding/sessions/${sessionId}/mcp-servers`, { servers }),
    onSuccess: (_data, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: codingKeys.mcpServers(sessionId) });
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    },
  });
}

export function useSetCodingAutoClear() {
  const queryClient = useQueryClient();
  return useMutation({
    // FRONT-3301: `thresholdPercent: null` clears the session's own pick, so it follows the default.
    mutationFn: ({ sessionId, ...body }: { sessionId: string; enabled: boolean; thresholdPercent?: number | null }) =>
      api.post<{ autoClearEnabled: boolean; autoClearThresholdPercent: number }>(
        `/api/coding/sessions/${sessionId}/auto-clear`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    },
  });
}
